// Must stay first: any module below can pull in the SDK, which resolves its
// Cognito pool and endpoints on import.
import "@/js/sdkEnv";
import { createApp } from "vue";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";
import router from "./router";
import { prefetchRouteComponents } from "./router/prefetchRoutes";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";
import "aws-amplify/auth/enable-oauth-listener";
import { Hub } from "aws-amplify/utils";
import { configureAuth } from "@/js/awsconfig";
import "@/js/sdk"; // Side-effect: creates SDK singleton with this app's Cognito config via the env bridge in sdkEnv.ts.
import * as util from "@/js/utilities";
import { endSession } from "@/js/session";
import { useTour } from "@/composables/useTour";
import { monitoringBeforeBreadcrumb, monitoringBeforeSend, monitoringCaptureError } from "@/plugins/monitoring";
import posthog from "posthog-js";

configureAuth();

// No tier group yet = brand-new user (same signal the router uses below for /welcome).
const PROVISIONED_TIER_GROUPS = [
	"FreeTier",
	"AcademicTier",
	"DeveloperTier",
	"ProTier",
	"EnterpriseTier",
	"EnterpriseTierS",
	"EnterpriseTierM",
	"EnterpriseTierL",
	"VirtualServerTier",
	"Administrator",
];

// Google is the only federated provider, so a completed signInWithRedirect is always a Google auth.
async function captureGoogleAuthOutcome() {
	try {
		const session = await fetchAuthSession();
		const groups = (session.tokens?.accessToken?.payload?.["cognito:groups"] as string[]) || [];
		const isReturningUser = PROVISIONED_TIER_GROUPS.some((g) => groups.includes(g));
		if (isReturningUser) {
			posthog.capture("user_logged_in", { method: "google" });
		} else {
			posthog.capture("user_account_created", {
				method: "google",
				...util.signupAttributionProps(),
			});
		}
	} catch (error) {
		monitoringCaptureError(error, "Capture Google auth outcome");
	}
}

// Hosted UI OAuth callback handling. When Cognito redirects back with
// ?code=&state= Amplify exchanges the code asynchronously; strip the query
// params once it succeeds so the router guards don't see a transient state.
Hub.listen("auth", ({ payload }) => {
	switch (payload.event) {
		case "signInWithRedirect":
			window.history.replaceState({}, document.title, window.location.pathname);
			void captureGoogleAuthOutcome();
			break;
		case "signInWithRedirect_failure":
			monitoringCaptureError(payload.data?.error, "OAuth callback failed");
			window.location.assign("/login");
			break;
		case "tokenRefresh_failure":
			// The session cannot be renewed.
			void endSession();
			break;
	}
});

import "@/assets/fonts";

import "@/assets/tokens/color-primitives.css";
import "@/assets/tokens/color-semantic.css";
import "@/assets/tokens/measurements.css";
import "@/assets/tokens/typography.css";
import "@/assets/tokens/spacing.css";
import "@/assets/tokens/layout.css";
import "@/assets/global.css";

/*
 * Segment Setup
 */

// segmentInit();

/*
 * Posthog Setup
 */

import posthogPlugin from "@/plugins/posthog"; //import the plugin.

/*
 * Hotjar Setup
 */

import Hotjar from "@hotjar/browser";
if (import.meta.env.PROD) {
	const siteId = 3501471;
	const hotjarVersion = 6;
	Hotjar.init(siteId, hotjarVersion);
}

/*
 * Sentry Setup
 */

import * as Sentry from "@sentry/vue";

/*
 * Pinia Setup
 */

// vue gets stuck in a loop if this goes in stores/root.js
import { createPinia } from "pinia";
const pinia = createPinia();

/*
 * Signaloid Client Setup
 */

import { registerTokens, registerRefreshTokenCallbacks } from "./js/signaloidClient";

//*	Register token refreshing interceptors for axios instances
registerTokens(
	fetchAuthSession()
		.then((sess) => sess.tokens?.accessToken?.toString() ?? "")
		.catch(() => ""),
	fetchAuthSession()
		.then((sess) => sess.tokens?.idToken?.toString() ?? "")
		.catch(() => ""),
);
registerRefreshTokenCallbacks(
	async () => {
		const session = await fetchAuthSession({ forceRefresh: true });
		return session.tokens?.accessToken;
	},
	async () => {
		const session = await fetchAuthSession({ forceRefresh: true });
		return session.tokens?.idToken;
	},
	endSession,
);

/*
 * Vue Router Setup
 */

//These are here because Auth is not initiated in the router file.

//*  Check authentication and subscription status for user
router.beforeEach(async (to) => {
	if (!to.matched.some((record) => record.meta.requiresAuth)) {
		return true;
	}

	try {
		await getCurrentUser();
		if (to.path.startsWith("/billing") || to.path.startsWith("/welcome")) {
			return true;
		}

		const session = await fetchAuthSession();
		const groups = (session.tokens?.accessToken?.payload?.["cognito:groups"] as string[]) || [];

		if (PROVISIONED_TIER_GROUPS.some((g) => groups.includes(g))) {
			return true;
		}

		return "/welcome";
	} catch {
		await endSession();
		return false;
	}
});

/*
 *	Guided tour navigation guard: confirm before leaving mid-tour
 */
let tourGuard: ReturnType<typeof useTour> | null = null;
router.beforeEach((to, from, next) => {
	if (!tourGuard) tourGuard = useTour();
	if (tourGuard.tourState.isActive && to.path !== from.path) {
		tourGuard.confirmLeave(to, next);
		return;
	}
	next();
});

/*
 * Initialise Vue
 */
const app = createApp(App);

// Install plugins
app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(posthogPlugin);

// Initialize Sentry
Sentry.init({
	app,
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
	release: import.meta.env.VITE_VERSION,
	integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
	tracePropagationTargets: ["localhost", import.meta.env.VITE_BACKEND_API_URL as string],
	tracesSampleRate: 1.0,
	sendDefaultPii: true,
	tunnel: import.meta.env.VITE_PROXY_GATEWAY_URL ? import.meta.env.VITE_PROXY_GATEWAY_URL + "/sentry" : undefined,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	beforeBreadcrumb: monitoringBeforeBreadcrumb,
	beforeSend: monitoringBeforeSend,
});

// Mount app
app.mount("#app");
router.isReady().then(
	() => prefetchRouteComponents(router),
	() => undefined,
);
